<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { usePerformerStore } from '@/stores/performer';
import { useTeaserStore } from '@/stores/teaser';
import { useTeaserVideoStore } from '@/stores/teaserVideo';
import player from '@/components/video/player.vue'
import { defineProps, onBeforeMount, ref, computed, watch } from 'vue';
import { useCamStore } from '@/stores/cam';
import { useAlertsStore } from '@/stores/alerts';
import i18n from '@/translations';
import { seconds, getAvatar, getStaticImages } from '@/utils';
import { usePaymentStore } from '@/stores/payment';
import { useModalsStore } from '@/stores/modals';
import { useUserStore } from '@/stores/user';
import { useSidebarStore } from '@/stores/sidebar';
import { useRouter } from 'vue-router';
import { performerStatusColor } from '@/api/performer/utils';

import FlagForm from '../videochat/controls/flag-form/flag-form.vue';

const props = defineProps<{
    performer:number, type: "VOYEUR" | "VOYEURPEEK", location: string, position?: number,
}>();

const router = useRouter();
const userStore = useUserStore();
const videoStore = useTeaserVideoStore( props.performer );
const camStore = useCamStore();
const sidebar = useSidebarStore();
const performer = usePerformerStore().getById(props.performer);
const { toggleModal } = useModalsStore();
const { isFavorite, toggleFavorite, isSubscribed } = usePerformerStore();
const { switchTo, toggleReservation, isReserved, toggleShowReservations, getVoyeurs } =  useTeaserStore();
const { toggleSubscribe } = useUserStore();
const { account } = storeToRefs(userStore);
const { current, reservations, showReservations } = storeToRefs( useTeaserStore() );

const videoWidth = ref(640);
const videoHeight = ref(480);
const isLoading = ref(false);
const showFlagForm = ref(false);
const scroll = ref(userStore.mobile ? true : false);
const destroy = ref(false);

function onMetadata( value: { width:number, height:number }){
    videoHeight.value = value.height;
    videoWidth.value = value.width;
}

const status = computed( ()=>{
    if ((!['idle', 'ended', 'error', 'choosing_payment'].includes(camStore.status))  && camStore.performer == performer.id){
        return camStore.status;
    }
    return performer.services.cam.status;
})

const allowedDestroy = computed( ()=>{
    return getVoyeurs(true).length < Object.values(current.value).length;
})

function switching(performerId: any){
    try {
        switchTo(performerId);
        // Mobile sidebar
        if ((userStore.mobile && sidebar.collapse)) {
            sidebar.switchState();
        }
    } catch {
        return;
    }

    router.replace({ name: 'teaser', params: { advert: performer.advertNumber} });
}

async function invite(){
    const alerts = useAlertsStore()
    isLoading.value = true;
    
    const error = await camStore.initiate(performer.id, {
        ivrCode: usePaymentStore().code,
        name: useUserStore().account?.username,
        type: 'chat'
    } );

    isLoading.value = false;
    if (error){
        alerts.openMessage( {
            translate: false,
            content: error.message,
            class: 'error',
            displayTime: seconds(5)
        })
    } else {
        alerts.openMessage({
            content: `${i18n.global.t('profile.session.wait')} ${performer.nickname}. ${i18n.global.t('profile.session.waiting')}`,
            translate: false,
            class: 'info',
            displayTime: seconds(8)
        })
    }
}

function change(){
    if (camStore.performer == performer.id){
        camStore.cancel();
    }
    destroy.value = true;
    videoStore.destroy(450);
}

function getPerformerReserve(id:number){
    return usePerformerStore().getById(id);
}

async function toggleFavorites(performerid: number) {
    if (!performerid) return;
    await toggleFavorite(performerid);
}

async function toggleNotifications(performerid: number) {
    if (!performerid) return;
    await toggleSubscribe(account.value.id!, performerid);
}

async function closeSession() {
    toggleModal('close', true, { name: 'profile', params: { advert: performer.advertNumber } });
}
</script>

<template>
    <div v-if="props.location === 'sidebar'" :style="{ aspectRatio: `${videoWidth} / ${videoHeight}`}">
        <div class="tease__sideTile-info"><span>{{ performer.nickname }}</span></div>
        <div class="tease__sideTile-status"><span :class="'status ' + performerStatusColor(performer)"></span></div>
        <div class="tease__sideTile-vid" v-bind:style="{ 'background-image': 'url(' + getStaticImages('loading', 'png') + ')' }">
            <player v-if="videoStore.playStream" v-bind="videoStore.playStream" :audio="'off'" :debug="false" @status-change="videoStore.playStreamStatusChange" @click="switching(props.performer)" @metadata="onMetadata" ></player>
        </div>
        <div class="tease__sideTile-advert" v-if="videoStore.playStream">{{ performer.advertNumber }}</div>
        <!--div class="tease__sideTile-change" :class="{'disabled': destroy }" @click="!destroy ? change() : ''"></div-->
    </div>  
    <div v-if="props.location === 'main'" :style="{ aspectRatio: `${videoWidth} / ${videoHeight}`}" class="tease__mainTile">
        <div class="tease__mainTile-vid" v-bind:style="{ 'background-image': 'url(' + getStaticImages('loading', 'png') + ')' }">
            <player :class="{'scroll': scroll }" v-if="videoStore.playStream" v-bind="videoStore.playStream" :audio="'off'" :debug="false" @status-change="videoStore.playStreamStatusChange" @metadata="onMetadata" >
            </player>
        </div>
        <div class="tease__mainTile-controls">
            <div class="controls">
                <div class="controls__close fa-solid fa-xmark close" @click="closeSession()"></div>
                <div class="controls__top">
                    <div class="controls__top-avatar" :style="{ 'background-image': `url(${getAvatar(performer)})`}"></div>
                    <div class="controls__top-customer">
                        <h2>{{ performer!.nickname }}</h2>
                        <span>{{ performer!.advertNumber }}</span>
                    </div>
                </div>
                <div class="controls__bottom">
                    <div class="controls__item fa-solid fa-bell"  :class="{ active: isSubscribed(performer.id) }" @click.stop.prevent="toggleNotifications(performer.id)"></div>
                    <div class="controls__item fa-solid fa-heart" :class="{ active: isFavorite(performer.id) }" @click="toggleFavorites(performer.id)"></div>
                    <div class="controls__item fa-solid fa-flag" :class="{ active: showFlagForm }" @click="showFlagForm = !showFlagForm">
                        <flag-form v-if="showFlagForm" @update:visible="showFlagForm = $event" />
                    </div>
                    <div class="controls__item fa-solid fa-clock" :class="{ active: showReservations }" @click="toggleShowReservations()">
                        <div class="tease__reservations" v-if="showReservations" @update:visible="showReservations = $event" @click.stop>
                            <div class="tease__reservations-item">
                                <span>{{ $t('teaser.reservations') }}</span>
                                <span>{{ $t('teaser.wait') }}</span>
                                <span></span>
                            </div>
                            <div class="tease__reservations-item" v-for="(reserve, index) in reservations" v-if="showReservations">
                                <span>{{ getPerformerReserve(reserve).nickname }} <i>({{ getPerformerReserve(reserve).advertNumber }})</i></span>
                                <span>( {{index+1}} )</span>
                                <span><i class="fa-solid fa-xmark" @click="toggleReservation(reserve)"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="tease__mainTile-nav">
            <div class="tease__mainTile-nav-main" v-if="status != 'idle'">
                <button class="cancel" :disabled="destroy || allowedDestroy" :class="{'disabled': destroy || allowedDestroy }" @click="!destroy ? change() : ''"></button>
                <button v-if="status=='available'" class="available" :disabled="isLoading" :class="{'disabled': isLoading }" @click="invite()">{{ $t('teaser.webcam') }}</button>
                <button v-else-if="status=='on'" class="busy" :class="{'reserved': isReserved(performer.id) }" @click="toggleReservation(performer.id)">{{ isReserved(performer.id) ? $t('teaser.cancelReserve') : $t('teaser.reserve') }}</button>
                <button v-else-if="status != 'off'" class="remove" @click="camStore.cancel"></button>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import './../videochat/controls/controls.scss';
</style>